import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveProjectResourceAllocations,
  useTransferProjectResourceAllocations,
  useProjectResourcesRetrieval,
} from "@planzone/view-models";
import { ProjectResource, Task } from "@planzone/dto";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Checkbox } from "@common/checkbox";
import { ResourceAvatar } from "@common/resourceAvatar";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { QuickAllocationMenu } from "@common/quickAllocationMenu";
import { List } from "./list";
import { Resource } from "./resource";
import { Context } from "./utils";
import { Identifiable } from "@planzone/dto";
import { ToggleArrow } from "@common/toggleArrow";
import { Props } from "./types";
import styles from "./styles.scss";
import classNames from "classnames";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";

function TransferProjectResourceAllocations({
  project,
  fromProjectResource,
  cancel,
}: Props) {
  const { t } = useTranslation();
  const [toProjectResource, setToProjectResource] =
    React.useState<Identifiable<ProjectResource> | null>(null);
  const { isRequestPending, transferProjectResourceAllocations } =
    useTransferProjectResourceAllocations(
      project,
      fromProjectResource,
      toProjectResource || fromProjectResource,
    );
  const {
    isRequestPending: isAssignmentsLoading,
    isRequestSuccess: areAssignmentsLoaded,
    isRequestFailure: hasAssignmentsFailed,
    isDirty,
    resourceAllocations,
    retrieveProjectResourceAllocations,
  } = useRetrieveProjectResourceAllocations(project, fromProjectResource);
  const { projectResources } = useProjectResourcesRetrieval(project);
  const [selectedTasks, setSelectedTasks] = useState<Identifiable<Task>[]>([]);
  const [toggled, setToggled] = React.useState<{ [key: string]: boolean }>({});

  const open = (id: string) => setToggled((prev) => ({ ...prev, [id]: true }));
  const close = (id: string) =>
    setToggled((prev) => ({ ...prev, [id]: false }));

  const toggleTaskSelection = (task: Identifiable<Task>) => {
    setSelectedTasks((prev) => {
      const exists = prev.some((t) => t.uuid === task.uuid);
      if (exists) {
        return prev.filter((t) => t.uuid !== task.uuid);
      } else {
        return [...prev, task];
      }
    });
  };

  const getAllTasksFromList = (list: any): Identifiable<Task>[] => {
    let tasks: Identifiable<Task>[] = [...list.tasks];
    list.subLists.forEach((subList: any) => {
      tasks = [...tasks, ...getAllTasksFromList(subList)];
    });
    return tasks;
  };

  const toggleAllTasks = () => {
    if (!areAssignmentsLoaded || !resourceAllocations?.resource?.lists) return;
    const allTasks = resourceAllocations.resource.lists.flatMap((list) =>
      getAllTasksFromList(list),
    );
    const allSelected = allTasks.every((task) =>
      selectedTasks.some((t) => t.uuid === task.uuid),
    );

    allSelected ? setSelectedTasks([]) : setSelectedTasks(allTasks);
  };

  const toggleListTasks = (list: any) => {
    const listTasks = getAllTasksFromList(list);
    const allSelected = listTasks.every((task) =>
      selectedTasks.some((t) => t.uuid === task.uuid),
    );

    if (allSelected) {
      setSelectedTasks((prev) =>
        prev.filter((t) => !listTasks.some((task) => task.uuid === t.uuid)),
      );
    } else {
      setSelectedTasks((prev) => [
        ...prev.filter((t) => !listTasks.some((task) => task.uuid === t.uuid)),
        ...listTasks,
      ]);
    }
  };

  const hasTasks =
    areAssignmentsLoaded &&
    resourceAllocations?.resource?.lists?.length > 0 &&
    resourceAllocations.resource.lists.flatMap((list) =>
      getAllTasksFromList(list),
    ).length > 0;

  React.useEffect(() => {
    if (
      (!isAssignmentsLoading &&
        !areAssignmentsLoaded &&
        !hasAssignmentsFailed) ||
      (isDirty && !isAssignmentsLoading)
    ) {
      retrieveProjectResourceAllocations({
        project,
        projectResource: fromProjectResource,
      });
    }
  }, [project.uuid, isDirty, fromProjectResource.uuid]);

  return (
    <Context.Provider value={{ toggled, open, close }}>
      <Modal
        header={t("transferProjectResourceAllocations.title")}
        close={cancel}
      >
        <div className={styles.transferProjectResourceAllocations}>
          <div className={styles.main}>
            <div className={styles.content}>
              <div className={styles.group}>
                <p className={styles.warning}>
                  {t("transferProjectResourceAllocations.warning")}
                </p>
              </div>
              <div className={styles.group}>
                <p className={styles.text}>
                  {t("transferProjectResourceAllocations.from")}
                </p>
                <Resource resource={fromProjectResource.resource} />
              </div>
              <div className={styles.group}>
                <p className={styles.text}>
                  {t("transferProjectResourceAllocations.to")}
                </p>
                <VisibilityWatcher>
                  {(isVisible, show, hide) => (
                    <div
                      className={classNames(styles.allocationWrapper, {
                        [styles.openedInput]: isVisible,
                      })}
                    >
                      <div
                        className={styles.input}
                        onClick={(e) => {
                          isVisible && e.stopPropagation();
                          isVisible && hide();
                          !isVisible && show();
                        }}
                      >
                        {toProjectResource ? (
                          <React.Fragment>
                            <div className={styles.resourceAvatar}>
                              <ResourceAvatar
                                resource={toProjectResource.resource}
                              />
                            </div>
                            <p>{toProjectResource.resource.name}</p>
                            <ToggleArrow className={styles.toggleIcon} />
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <p>
                              {t(
                                "transferProjectResourceAllocations.selectDestination",
                              )}
                            </p>
                            <ToggleArrow className={styles.toggleIcon} />
                          </React.Fragment>
                        )}
                      </div>
                      {isVisible && (
                        <QuickAllocationMenu
                          project={project}
                          className={styles.quickAllocationMenu}
                          filters={[fromProjectResource.resource]}
                          selectResource={(resource) => {
                            const selectedProjectResource =
                              projectResources.find(
                                (projectResource) =>
                                  projectResource.resource.uuid ===
                                  resource.uuid,
                              );
                            selectedProjectResource &&
                              setToProjectResource(selectedProjectResource);
                            hide();
                          }}
                        />
                      )}
                    </div>
                  )}
                </VisibilityWatcher>
              </div>
              <div className={styles.listsContainer}>
                {areAssignmentsLoaded && hasTasks && (
                  <div className={styles.selectAll}>
                    <label className={styles.toggle}>
                      <div
                        className={styles.checkbox}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAllTasks();
                        }}
                      >
                        <Checkbox
                          isChecked={
                            resourceAllocations?.resource?.lists?.length > 0 &&
                            resourceAllocations.resource.lists
                              .flatMap((list) => getAllTasksFromList(list))
                              .every((task) =>
                                selectedTasks.some((t) => t.uuid === task.uuid),
                              )
                          }
                        />
                      </div>
                      <p>
                        {t("transferProjectResourceAllocations.selectAllTasks")}
                      </p>
                    </label>
                  </div>
                )}
                <ScreenWatcher
                  renderChildren={({ height }) => (
                    <LayoutWatcher
                      renderChildren={({ top }) =>
                        areAssignmentsLoaded && (
                          <>
                            {!hasTasks ? (
                              <p className={styles.noTasks}>
                                {t(
                                  "transferProjectResourceAllocations.noTasks",
                                )}
                              </p>
                            ) : (
                              resourceAllocations?.resource?.lists?.map(
                                (list) => (
                                  <div
                                    key={list.uuid}
                                    style={{
                                      maxHeight: height - top - 200,
                                      overflow: "auto",
                                    }}
                                  >
                                    <List
                                      list={list}
                                      project={project}
                                      resource={fromProjectResource.resource}
                                      depth={0}
                                      isRoot
                                      selectedTask={selectedTasks}
                                      toggleTaskSelection={toggleTaskSelection}
                                      toggleListTasks={toggleListTasks}
                                    />
                                  </div>
                                ),
                              )
                            )}
                          </>
                        )
                      }
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.buttons}>
              <Button type="cancel" press={cancel}>
                {t("transferProjectResourceAllocations.cancel")}
              </Button>
              <Button
                type="submit"
                status={isRequestPending ? "pending" : "none"}
                isDisabled={isRequestPending || !toProjectResource}
                press={() =>
                  toProjectResource &&
                  transferProjectResourceAllocations({
                    project,
                    fromProjectResource,
                    toProjectResource,
                    tasks: selectedTasks,
                  })
                }
              >
                {t("transferProjectResourceAllocations.confirm")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Context.Provider>
  );
}

export { TransferProjectResourceAllocations };
